// src/components/TripRating.js
import React, { useState, useEffect } from 'react';
import { rateTripByUser, getTripRatings, getUserTripRating } from '../services/tripRatingService';
import { showSuccess, showError } from '../services/alertService';
import './TripRating.css';

const TripRating = ({ trip, user }) => {
  const [userRating, setUserRating] = useState(null);
  const [allRatings, setAllRatings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showRatingForm, setShowRatingForm] = useState(false);
  const [newRating, setNewRating] = useState(0);
  const [newComment, setNewComment] = useState('');
  const [hoveredStar, setHoveredStar] = useState(0);

  useEffect(() => {
    if (trip.id && user) {
      fetchRatings();
    }
  }, [trip.id, user]);

  const fetchRatings = async () => {
    try {
      setLoading(true);
      const [userRatingData, allRatingsData] = await Promise.all([
        getUserTripRating(trip.id, user.uid),
        getTripRatings(trip.id)
      ]);
      
      setUserRating(userRatingData);
      setAllRatings(allRatingsData);
      
      if (userRatingData) {
        setNewRating(userRatingData.rating);
        setNewComment(userRatingData.comment || '');
      }
    } catch (error) {
      console.error('Error fetching ratings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRating = async (e) => {
    e.preventDefault();
    if (newRating === 0) {
      await showError(
        '請選擇評分',
        '請先點擊星星來選擇你的評分。'
      );
      return;
    }

    try {
      setLoading(true);
      await rateTripByUser(trip.id, user.uid, newRating, newComment);
      await fetchRatings();
      setShowRatingForm(false);
      
      // 顯示成功提示
      const isUpdate = userRating !== null;
      await showSuccess(
        isUpdate ? '評分已更新！' : '評分提交成功！',
        isUpdate 
          ? '你的評分已成功更新，感謝你的反饋！' 
          : `你給了這個行程 ${newRating} 星評分，感謝你的評價！`
      );
    } catch (error) {
      console.error('Error submitting rating:', error);
      await showError(
        '提交失敗',
        '提交評分時發生錯誤，請檢查網路連線後再試。'
      );
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating, interactive = false, size = 'normal') => {
    const stars = [];
    const displayRating = interactive ? (hoveredStar || newRating) : rating;
    
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span
          key={i}
          className={`star ${size} ${i <= displayRating ? 'filled' : ''} ${interactive ? 'interactive' : ''}`}
          onClick={interactive ? () => setNewRating(i) : undefined}
          onMouseEnter={interactive ? () => setHoveredStar(i) : undefined}
          onMouseLeave={interactive ? () => setHoveredStar(0) : undefined}
        >
          ★
        </span>
      );
    }
    return stars;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = dateString.seconds ? new Date(dateString.seconds * 1000) : new Date(dateString);
    return date.toLocaleDateString('zh-TW');
  };

  // 如果是行程擁有者，顯示不同的標題和說明
  const isOwner = trip.uid === user.uid;

  return (
    <div className="trip-rating">
      <h3>{isOwner ? '為你的行程評分' : '行程評分'}</h3>
      
      {/* 整體評分顯示 */}
      <div className="overall-rating">
        <div className="rating-summary">
          <div className="average-rating-large">
            <span className="rating-number">
              {trip.tripRating?.averageRating || 0}
            </span>
            <div className="stars-large">
              {renderStars(trip.tripRating?.averageRating || 0, false, 'large')}
            </div>
          </div>
          <div className="rating-info">
            <p>{trip.tripRating?.ratingCount || 0} 人評分</p>
            <p>{isOwner ? '整體評分' : '平均評分'}</p>
          </div>
        </div>
      </div>

      {/* 用戶評分區域 */}
      <div className="user-rating-section">
        {userRating ? (
          <div className="existing-rating">
            <h4>{isOwner ? '你的評分' : '你的評分'}</h4>
            <div className="user-rating-display">
              <div className="stars">
                {renderStars(userRating.rating)}
              </div>
              <span className="rating-text">({userRating.rating}/5)</span>
            </div>
            {userRating.comment && (
              <p className="user-comment">"{userRating.comment}"</p>
            )}
            <button 
              onClick={() => setShowRatingForm(true)}
              className="edit-rating-btn"
            >
              修改評分
            </button>
          </div>
        ) : (
          <div className="no-rating">
            <h4>{isOwner ? '為你的行程評分' : '為這個行程評分'}</h4>
            <p>{isOwner ? '分享你對這次旅行的整體感受' : '分享你對這個行程的看法'}</p>
            <button 
              onClick={() => setShowRatingForm(true)}
              className="rate-btn"
            >
              立即評分
            </button>
          </div>
        )}
      </div>

      {/* 評分表單 */}
      {showRatingForm && (
        <div className="rating-form-overlay">
          <div className="rating-form">
            <h4>{userRating ? '修改評分' : '為行程評分'}</h4>
            <form onSubmit={handleSubmitRating}>
              <div className="rating-input">
                <label>評分 *</label>
                <div className="stars-input">
                  {renderStars(newRating, true)}
                </div>
                <span className="rating-label">
                  {newRating > 0 && `${newRating} 星`}
                </span>
              </div>
              
              <div className="comment-input">
                <label>評論 (可選)</label>
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="分享你對這個行程的想法..."
                  rows="3"
                  maxLength="200"
                />
                <div className="char-count">
                  {newComment.length}/200
                </div>
              </div>
              
              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={() => setShowRatingForm(false)}
                  className="cancel-btn"
                >
                  取消
                </button>
                <button 
                  type="submit"
                  disabled={loading || newRating === 0}
                  className="submit-btn"
                >
                  {loading ? (
                    <>
                      <span className="alert-loading"></span>
                      提交中...
                    </>
                  ) : userRating ? '更新評分' : '提交評分'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 其他用戶評分列表 */}
      {allRatings.length > 0 && (
        <div className="all-ratings">
          <h4>用戶評價 ({allRatings.length})</h4>
          <div className="ratings-list">
            {allRatings.slice(0, 5).map(rating => (
              <div key={rating.id} className="rating-item">
                <div className="rating-header">
                  <div className="stars">
                    {renderStars(rating.rating)}
                  </div>
                  <span className="rating-date">
                    {formatDate(rating.createdAt)}
                  </span>
                </div>
                {rating.comment && (
                  <p className="rating-comment">"{rating.comment}"</p>
                )}
              </div>
            ))}
            {allRatings.length > 5 && (
              <p className="more-ratings">
                還有 {allRatings.length - 5} 則評價...
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TripRating;